'use client'
import {ChevronDownIcon} from '@heroicons/react/24/solid'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import Link from 'next/link'
import {useLocale, useTranslations} from 'next-intl'

import {buttonClasses} from '@/app/[locale]/(website)/components/ui/ButtonClasses'
import {LocalizedPath} from '@/app/[locale]/(website)/helpers/getAlternates'
import {Locale, routing} from '@/i18n/routing'

interface LocaleSwitcherProps {
    navigation: LocalizedPath
}

function handlePointerMove(event: React.PointerEvent) {
    event.preventDefault()
}

function handlePointerLeave(event: React.PointerEvent) {
    event.preventDefault()
}

export default function LocaleSwitcher({navigation}: LocaleSwitcherProps) {
    const currentLocale = useLocale()
    const t = useTranslations('LocaleSwitcher')

    const localeLinks = routing.locales.map((locale: Locale) => {
        const path = navigation[locale]
        const href = path ? `/${path}` : `/${locale}`
        return {
            locale,
            label: t(locale),
            href,
            isCurrent: locale === currentLocale,
        }
    })

    return (
        <NavigationMenu.Root orientation="horizontal">
            <NavigationMenu.List>
                <NavigationMenu.Item className="relative">
                    <NavigationMenu.Trigger
                        className={`group ${buttonClasses({
                            variant: 'navigation',
                            size: 'medium',
                            shape: 'rounded',
                            width: 'full',
                        })}`}
                        onPointerMove={handlePointerMove}
                        onPointerLeave={handlePointerLeave}
                    >
                        <span>{t('label')}</span>
                        <ChevronDownIcon
                            className="size-4 mr-0.5 relative text-text-secondary transition-transform duration-300 ease-in-out group-data-[state=open]:-rotate-180"
                            aria-hidden
                        />
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="absolute top-full bg-bg-primary mt-1 w-max min-w-[200px] overflow-hidden rounded-xl backdrop-blur-3xl border border-border-secondary shadow-lg right-0 lg:left-0 lg:right-auto animate-scale-in">
                        <ul className="flex flex-col p-2 gap-1">
                            {localeLinks.map(({locale, label, href, isCurrent}) => (
                                <li key={`language_${locale}`}>
                                    <NavigationMenu.Link asChild>
                                        <Link
                                            href={isCurrent ? '#' : href}
                                            aria-disabled={isCurrent}
                                            className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-md transition-colors ${
                                                isCurrent
                                                    ? 'text-text-quaternary cursor-not-allowed'
                                                    : 'text-text-secondary hover:bg-bg-quinary'
                                            }`}
                                            hrefLang={locale}
                                            lang={locale}
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="text-xs uppercase text-text-quaternary w-7">
                                                    {locale}
                                                </span>
                                                <span>{label}</span>
                                            </span>
                                        </Link>
                                    </NavigationMenu.Link>
                                </li>
                            ))}
                        </ul>
                    </NavigationMenu.Content>
                </NavigationMenu.Item>
            </NavigationMenu.List>
        </NavigationMenu.Root>
    )
}
